import { useState } from 'react';
import { cartTotal, unitPrice } from '../lib/cart';
import { navigate } from '../lib/router';
import { baht, main, second } from '../lib/text';
import type { MenuItem } from '../lib/types';
import { DishSheet } from './DishSheet';
import type { GuestState } from './GuestApp';
import { Header } from './Header';

/** The menu as a tray to fill: category chips, dish cards with stickers, and the tray bar at the bottom. */
export function Tray({ s }: { s: GuestState }) {
  const cats = s.menu.categories;
  const [cat, setCat] = useState(cats[0]?.id ?? '');
  const [open, setOpen] = useState<MenuItem | null>(null);
  const shown = cats.find((c) => c.id === cat)?.items ?? [];
  const count = s.cart.reduce((n, l) => n + l.qty, 0);
  const total = cartTotal(s.cart, s.items);
  const qtyOf = (id: string) => s.cart.filter((l) => l.itemId === id).reduce((n, l) => n + l.qty, 0);
  const rounds = s.orders.length;

  const quickAdd = (item: MenuItem) => {
    if ((item.options ?? []).some((g) => g.min > 0) || (item.options ?? []).length) setOpen(item);
    else s.add(item.id, {}, 1);
  };

  return (
    <main className="screen">
      <Header s={s} sub={`Dine-in · Round ${rounds + 1}`} />
      <div className="row" style={{ padding: '0 20px 0 0' }}>
        <h1 className="title">อยากกินอะไรดี? <span className="muted" style={{ display: 'block', fontSize: 15, fontFamily: 'var(--body)', fontWeight: 500 }}>What goes on your tray?</span></h1>
        {rounds > 0 && (
          <button className="pill" style={{ flex: 'none' }} onClick={() => navigate('/t/orders')}>My orders · {rounds}</button>
        )}
      </div>

      <nav aria-label="Categories" className="chips">
        {cats.map((c) => (
          <button key={c.id} className="chip" aria-pressed={c.id === cat} onClick={() => setCat(c.id)}>{main(c.name)}</button>
        ))}
      </nav>

      <div className="grid">
        {shown.map((item) => {
          const sold = s.soldOut.has(item.id);
          const q = qtyOf(item.id);
          const art = item.media?.sticker ?? item.media?.photo;
          return (
            <article key={item.id} className={`card${sold ? ' sold' : ''}`}>
              <button className="art" onClick={() => !sold && setOpen(item)} aria-label={`More about ${main(item.name)}`} disabled={sold}>
                {art ? <img className="sticker" src={art} alt="" loading="lazy" /> : <div className="plate"><span /></div>}
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 52 }}>
                <span className="name-th">{main(item.name)}</span>
                <span className="name-en">{second(item.name)}</span>
              </div>
              <div className="row">
                <span className="price">{baht(unitPrice(item, {}))}</span>
                {sold ? <span className="soldtag">หมด · Sold out</span> : (
                  <button className={`add${q ? ' has' : ''}`} onClick={() => quickAdd(item)} aria-label={`Add ${main(item.name)}`}>{q ? `×${q} +` : '+'}</button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <section className="tray" aria-label="Your tray">
        {s.error && (
          <div className="warn" role="alert" style={{ color: 'var(--ink)' }}>
            {s.error} <button className="ghost" style={{ color: 'var(--ink)', borderColor: 'var(--ink)', marginLeft: 8, minHeight: 32, padding: '2px 10px' }} onClick={s.clearError}>OK</button>
          </div>
        )}
        {count === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 4px' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px dashed var(--mango)' }} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="display" style={{ fontWeight: 500, fontSize: 16 }}>ถาดยังว่าง · Your tray is empty</span>
              <span style={{ fontSize: 13, color: '#C8CCE0' }}>Tap + on a dish to add it</span>
            </div>
          </div>
        ) : (
          <>
            <TrayLines s={s} />
            <div className="row">
              <span style={{ fontWeight: 600, fontSize: 14 }}>{count} รายการ · dishes</span>
              <span className="display" style={{ color: 'var(--mango)', fontSize: 20 }}>{baht(total)}</span>
            </div>
            <button className="cta" onClick={() => void s.send()} disabled={s.sending}>
              {s.sending ? 'กำลังส่ง… · Sending…' : 'ส่งเข้าครัว · Send to kitchen'}
            </button>
          </>
        )}
      </section>

      {open && <DishSheet item={open} s={s} onClose={() => setOpen(null)} />}
    </main>
  );
}

/** Compact, editable list of what's on the tray (tap - / + to change). */
function TrayLines({ s }: { s: GuestState }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '28dvh', overflowY: 'auto' }}>
      {s.cart.map((l) => {
        const it = s.items.get(l.itemId);
        if (!it) return null;
        const extras = (it.options ?? []).flatMap((g) => (l.choices[g.id] ?? []).map((id) => main(g.choices.find((c) => c.id === id)?.name)));
        return (
          <li key={l.key} className="row" style={{ gap: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{main(it.name)}</span>
              {(extras.length > 0 || l.note) && <span style={{ fontSize: 12, color: '#C8CCE0' }}>{[...extras, l.note].filter(Boolean).join(' · ')}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
              <button className="ghost" style={{ minHeight: 36, padding: '0 10px' }} onClick={() => s.setQty(l.key, l.qty - 1)} aria-label={`One less ${main(it.name)}`}>−</button>
              <span className="display" style={{ minWidth: 20, textAlign: 'center' }}>{l.qty}</span>
              <button className="ghost" style={{ minHeight: 36, padding: '0 10px' }} onClick={() => s.setQty(l.key, l.qty + 1)} aria-label={`One more ${main(it.name)}`}>+</button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
